import type { Me, MyAward, MyRecognition, SpotEntryConfig } from '@myrewards/contracts';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { Link } from 'react-aria-components';
import { photos } from '../assets';
import { TileGrid, TwoColumns } from '../shell';
import {
  Card,
  CardHelp,
  cx,
  DataTable,
  EmptyArt,
  formatCount,
  formatDate,
  formatMoney,
  formatShortDate,
  Icon,
  plural,
  Stat,
  StatusPill,
  type DataColumn,
} from '../ui';
import { AWARD_TYPE_LABEL, CASH_ONLY, isCashOnly, STAGE } from './labels';
import { SpotCard } from './SpotCard';
import s from './Home.module.css';
import { routeCopy } from '../spot/policy-copy';

const DOC_LABEL = { letter: 'Letter', earning_statement: 'Earning statement' } as const;

function stageOf(award: MyAward): { label: string; tone: 'waiting' | 'info' | 'done' } {
  if (award.stage === 'employee_choice' && award.electionDue) {
    return { label: `Election due ${formatShortDate(award.electionDue)}`, tone: 'waiting' };
  }
  const stage = STAGE[award.stage];
  return { label: stage.label, tone: stage.tone === 'done' ? 'done' : 'info' };
}

const COLUMNS: readonly DataColumn<MyAward>[] = [
  {
    id: 'date',
    header: 'Award date',
    cell: (row) => <span className={s.muted}>{formatDate(row.awardDate)}</span>,
    nowrap: true,
  },
  {
    id: 'award',
    header: 'Award',
    cell: (row) => (
      <>
        <Link href={`/my-rewards/${row.lineId}`} className={s.strongLink}>
          {AWARD_TYPE_LABEL[row.type]}
        </Link>
        <span className={s.subId}>{row.award.displayId}</span>
      </>
    ),
  },
  { id: 'cash', header: 'Cash', cell: (row) => formatMoney(row.cashCents), numeric: true },
  {
    id: 'options',
    header: 'Options',
    cell: (row) =>
      row.options > 0 ? (
        formatCount(row.options)
      ) : (
        <span className={s.muted}>{isCashOnly(row.type) ? CASH_ONLY : 'None'}</span>
      ),
    numeric: true,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => {
      const stage = stageOf(row);
      return <StatusPill tone={stage.tone} label={stage.label} />;
    },
  },
  {
    id: 'documents',
    header: 'Documents',
    cell: (row) =>
      // Never a blank cell (APP-093): an award without a file here says so.
      row.documents.length === 0 ? (
        <span className={s.muted}>No documents</span>
      ) : (
        <span className={s.docLinks}>
          {row.documents.map((doc, index) => (
            <span key={doc.kind}>
              {index > 0 && ' · '}
              <Link href={doc.href}>{DOC_LABEL[doc.kind]}</Link>
            </span>
          ))}
        </span>
      ),
  },
];

interface DocumentRow {
  key: string;
  name: string;
  meta: string;
  href: string;
}

function documentsOf(awards: readonly MyAward[]): DocumentRow[] {
  return awards
    .flatMap((award) =>
      award.documents.map((doc) => ({
        key: `${award.award.displayId}-${doc.kind}`,
        date: doc.date,
        name:
          doc.kind === 'letter'
            ? `${AWARD_TYPE_LABEL[award.type]} letter`
            : `Earning statement · ${award.award.displayId}`,
        meta: `PDF · ${formatDate(doc.date)}${doc.kind === 'earning_statement' ? ' · from Payroll' : ''}`,
        href: doc.href,
      })),
    )
    .sort((a, b) => b.date.localeCompare(a.date))
    .map(({ key, name, meta, href }) => ({ key, name, meta, href }));
}

/**
 * "Questions about an award?" — the navy contact block. It names the person when the server names
 * one, and the Stock Admin team otherwise; it never disappears (APP-093).
 */
export function ContactBlock({ contact }: { contact: MyRecognition['contact'] }): ReactElement {
  return (
    <section className={s.contact} aria-labelledby="home-contact">
      <div className={s.contactHead}>
        <h2 id="home-contact" className={cx('eyebrow', s.contactLabel)}>
          Questions about an award?
        </h2>
        <CardHelp
          topic="who to ask"
          onDark
          shows="The person who looks after awards and stock at Areté."
          canDo={`Contact ${contact === null ? 'the Stock Admin team' : contact.person.name} with any question about an award, a letter or your options.`}
        />
      </div>
      <div className={s.person}>
        <span className={s.personAvatar} aria-hidden="true">
          {contact === null ? <Icon name="people" size={20} /> : contact.person.initials}
        </span>
        <span className={s.personText}>
          <span className={s.personName}>
            {contact === null ? 'The Stock Admin team' : contact.person.name}
          </span>
          <span className={s.personRole}>
            {contact === null ? 'Awards, letters and stock options' : contact.title}
          </span>
        </span>
      </div>
    </section>
  );
}

/** E1 (and FR1 on the first visit): the Home of someone who holds no role panel. */
export function EmployeeHome({
  me,
  mine,
  spotApprovalMode,
  spotNomination,
  nothingYet,
  waiting,
  spot = null,
  howItWorksRef,
}: {
  me: Me;
  mine: MyRecognition;
  spotApprovalMode: SpotEntryConfig['approvalMode'] | undefined;
  spotNomination: boolean;
  nothingYet: boolean;
  waiting: ReactNode;
  /** APP-232: Spot awards in progress, under Waiting on you (the committee, System Admins). */
  spot?: ReactNode;
  howItWorksRef: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  const docs = documentsOf(mine.awards);
  const hasAwards = mine.awards.length > 0;
  const nextAward = mine.awards.find((a) => a.stage === 'employee_choice');

  const main = (
    <>
      {waiting}
      {spot}
      <Card
        title="My awards"
        helpAnchor="my-awards"
        {...(nothingYet && { accent: 'gold' as const })}
        help={{
          shows:
            'Every award you’ve received, newest first: what it was worth, where it is now and its documents.',
          canDo:
            'Open an award for its letter, payout and progress, or download a letter or earning statement.',
        }}
        aside="Awards appear here on their award date"
      >
        {hasAwards ? (
          <DataTable
            caption="My awards"
            columns={COLUMNS}
            rows={mine.awards}
            rowKey={(row) => row.award.id}
            minWidth={680}
            empty={{
              title: 'No awards yet.',
              body: 'When one arrives, you’ll see it here first, with its letter.',
            }}
          />
        ) : (
          <div className={s.firstAward}>
            <span className={s.firstAwardArt} aria-hidden="true">
              <EmptyArt name="award" />
            </span>
            <div className={s.firstAwardText}>
              <p className={cx('eyebrow', s.goldEyebrow)}>Nothing yet, and that’s normal</p>
              <h3 className={cx('disp', s.firstAwardTitle)}>
                Your awards will appear here on their award date — we’ll email you when one arrives.
              </h3>
              <p className={s.cardText}>
                Each one comes with its letter, and an earning statement once it’s paid.
              </p>
              {nothingYet && (
                <p className={s.cardText}>
                  <Link
                    className={s.strongLink}
                    onPress={() => {
                      howItWorksRef.current?.scrollIntoView({ block: 'start' });
                      howItWorksRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    See how recognition works at Areté
                  </Link>
                </p>
              )}
            </div>
          </div>
        )}
      </Card>
      {nothingYet ? (
        <FirstVisitCards
          howItWorksRef={howItWorksRef}
          windowDays={mine.electionWindowDays}
          spotApprovalMode={spotApprovalMode}
        />
      ) : (
        <TileGrid>
          <Stat
            label={
              mine.totals.since !== null
                ? `Your awards · since ${mine.totals.since}`
                : 'Your awards · so far'
            }
            value={plural(mine.totals.awards, 'award', 'awards')}
            {...(mine.totals.awards === 0 && { note: 'Your first award starts the count' })}
            help={{
              shows: 'How many awards you’ve received, counted from your first one.',
              canDo: 'See each award in My awards above.',
            }}
          />
          <Stat
            label="Your options · to date"
            value={formatCount(mine.totals.options)}
            note={
              nextAward && nextAward.options > 0
                ? `Vest over 4 years · ${formatCount(nextAward.options)} more with this ${nextAward.type}`
                : mine.totals.options > 0
                  ? 'Vest over 4 years'
                  : 'Some awards include stock options'
            }
            help={{
              shows:
                'Stock options granted to you across all your awards. Options vest over 4 years.',
              canDo: 'Open an award in My rewards to see its vesting.',
            }}
          />
          <Stat
            label="Your cash paid · to date"
            value={formatMoney(mine.totals.cashPaidCents)}
            note={
              nextAward
                ? `Before this year’s ${nextAward.type}`
                : 'Paid through Payroll, with an earning statement'
            }
            help={{
              shows:
                'Cash paid to you through Payroll across your awards. An award still in progress isn’t counted until it’s paid.',
              canDo: 'Download the earning statement for each payment from Documents.',
            }}
          />
        </TileGrid>
      )}
    </>
  );

  const side = (
    <>
      {nothingYet && <DetailsCard me={me} mine={mine} />}
      {spotNomination && <SpotCard spot={mine.spot} />}
      {!nothingYet && (
        <Card
          title="Documents"
          titleSize="sm"
          compact
          help={{
            shows: 'Your award letters and Payroll earning statements, kept in one place.',
            canDo: 'Download any of them as a PDF, whenever you need it.',
          }}
        >
          {docs.length === 0 ? (
            <EmptyStateDocs />
          ) : (
            <ul className={s.docs}>
              {docs.map((doc) => (
                <li key={doc.key}>
                  <Link href={doc.href} className={s.doc}>
                    <span className={s.docIcon}>
                      <Icon name="file" size={22} />
                    </span>
                    <span className={s.docText}>
                      <span className={s.docName}>{doc.name}</span>
                      <span className={s.docMeta}>{doc.meta}</span>
                    </span>
                    <Icon name="download" size={18} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
      <ContactBlock contact={mine.contact} />
    </>
  );

  if (!nothingYet) return <TwoColumns main={main} side={side} />;
  // FR1: on the first visit, the values behind every award and the first questions people ask.
  return (
    <div className={s.stack}>
      <TwoColumns main={main} side={side} />
      <ValuesSection />
      <GoodToKnow mine={mine} />
    </div>
  );
}

/** The five values behind every award (FR1). Photos are atmosphere only, never captioned. */
const VALUES = [
  { name: 'Creativity', src: photos.engineersAtWhiteboard },
  { name: 'Integrity', src: photos.navyMineDetectionAtSea },
  { name: 'Passion', src: photos.opticalFieldTesting },
  { name: 'Responsiveness', src: photos.engineerAtLaptopInAircraft },
  { name: 'Collaboration', src: photos.engineersAtWorkstation },
] as const;

function ValuesSection(): ReactElement {
  return (
    <section className={s.valuesSection} aria-labelledby="what-we-value">
      <div className={s.valuesHead}>
        <div className={s.plainCardHead}>
          <h2 id="what-we-value" className={cx('disp', s.plainCardTitle)}>
            What we value
          </h2>
          <CardHelp
            topic="What we value"
            shows="The five values behind every award at Areté."
            canDo="Nothing to do here. When you nominate someone, you say who, a short title and what they did, and you can name the value their work showed."
          />
        </div>
        <p className={s.meta}>The five values behind every award at Areté</p>
      </div>
      <ul className={s.valuesGrid}>
        {VALUES.map((value) => (
          <li key={value.name} className={s.valueTile}>
            <img src={value.src} alt="" className={s.valuePhoto} />
            <span className={s.valueWash} aria-hidden="true" />
            <span className={s.valueCaption}>
              <span className={s.valueBar} aria-hidden="true" />
              <span className={cx('disp', s.valueName)}>{value.name}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * FR1 "Good to know": the first three questions, answered from what's true for this person, never
 * a typed-in name: who looks after awards comes from the directory, as does their manager.
 */
function GoodToKnow({ mine }: { mine: MyRecognition }): ReactElement {
  const contact = mine.contact;
  const manager = mine.details.manager?.name ?? null;
  const ask = [
    contact
      ? `${contact.person.name}${contact.title ? `, our ${contact.title}` : ''}, for anything about an award, a letter or an election.`
      : 'The Stock Admin team, for anything about an award, a letter or an election.',
    manager
      ? `Your manager, ${manager}, for questions about recognition on your team.`
      : 'Your manager, for questions about recognition on your team.',
  ].join(' ');
  const faq = [
    {
      q: 'When will I see an award?',
      a: 'On its award date. We email you that day, and the award shows up here with its letter. If it includes a choice of cash or stock, the letter and this page say how long you have to decide.',
    },
    {
      q: 'What are options?',
      a: 'Some awards include stock options: the right to buy Areté shares later at a set price. Your letter shows how many options you received and how they vest.',
    },
    { q: 'Who do I ask?', a: ask },
  ];
  return (
    <section className={s.plainCard} aria-labelledby="good-to-know">
      <div className={s.plainCardHead}>
        <h2 id="good-to-know" className={cx('disp', s.plainCardTitle)}>
          Good to know
        </h2>
        <CardHelp
          topic="Good to know"
          shows="Answers to the questions people ask first."
          canDo="Open a question to read its answer. Help has more, any time."
        />
      </div>
      <div className={s.faqList}>
        {faq.map((item) => (
          <details key={item.q} className={s.faq}>
            <summary className={s.faqQuestion}>
              <span>{item.q}</span>
              <Icon name="chevronDown" size={18} />
            </summary>
            <p className={s.faqAnswer}>{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function EmptyStateDocs(): ReactElement {
  return (
    <div className={s.emptyDocs}>
      <EmptyArt name="letter" size="sm" />
      <h3 className={s.emptyTitle}>No documents yet</h3>
      <p className={s.cardText}>
        Your award letters and Payroll earning statements will be kept here, ready to download.
      </p>
    </div>
  );
}

/** FR1: what recognition looks like at Areté, before the first award. */
function FirstVisitCards({
  howItWorksRef,
  windowDays,
  spotApprovalMode,
}: {
  howItWorksRef: RefObject<HTMLHeadingElement | null>;
  /** The election window setting (FR1 states it, never a fixed number); absent from older servers. */
  windowDays: number | undefined;
  /** The active Spot route; unavailable configuration uses route-neutral guidance. */
  spotApprovalMode: SpotEntryConfig['approvalMode'] | undefined;
}): ReactElement {
  const spotCopy = routeCopy(spotApprovalMode);
  const spotRouteGuidance =
    spotApprovalMode === undefined ? spotCopy.whoDecides : spotCopy.amountMeta;
  return (
    <>
      <section className={s.plainCard} aria-labelledby="how-recognition-works">
        <div className={s.plainCardHead}>
          <h2
            id="how-recognition-works"
            ref={howItWorksRef}
            tabIndex={-1}
            className={cx('disp', s.plainCardTitle)}
          >
            How recognition works at Areté
          </h2>
          <CardHelp
            topic="How recognition works at Areté"
            shows="The ways your work can be recognized, and what comes with each award."
            canDo="Nothing to do yet. Your awards show up in My awards on their award date."
          />
        </div>
        <p className={s.cardText}>
          Three ways your work can be recognized. Every award has a letter, and some include a
          choice of cash or stock.
        </p>
        <ul className={s.ways}>
          <li>
            <strong>Spot Awards.</strong> Any colleague can nominate you for work that stood out.{' '}
            {spotRouteGuidance}
            {spotApprovalMode !== undefined && '.'}
          </li>
          <li>
            <strong>Sector Discretionary Bonus.</strong> Your Sector Lead recognizes great work in
            your sector, in cash and sometimes stock options.
          </li>
          <li>
            <strong>Professional and Team Achievement Awards.</strong> For standout achievements, on
            your own or with your team.
          </li>
        </ul>
      </section>
      <section className={s.plainCard} aria-labelledby="what-youll-do">
        <div className={s.plainCardHead}>
          <h2 id="what-youll-do" className={cx('disp', s.plainCardTitle)}>
            What you’ll be able to do
          </h2>
          <CardHelp
            topic="What you’ll be able to do"
            shows="What MyRewards lets you do once an award arrives."
            canDo="Nothing to do yet. The quick tour shows each of these on a practice copy."
          />
        </div>
        <div className={s.abilities}>
          <div className={s.ability}>
            <span className={s.abilityIcon} aria-hidden="true">
              <Icon name="tuning" size={22} />
            </span>
            <h3 className={s.abilityTitle}>Choose cash or stock, with a calculator</h3>
            <p className={s.cardText}>
              {windowDays === undefined
                ? 'When an award lets you choose, its letter gives the date to choose by.'
                : `When an award lets you choose, you’ll have ${plural(windowDays, 'day', 'days')} from the award date.`}{' '}
              The calculator shows the estimated tax, whole shares and cash left for each choice.
            </p>
          </div>
          <div className={s.ability}>
            <span className={s.abilityIcon} aria-hidden="true">
              <Icon name="download" size={22} />
            </span>
            <h3 className={s.abilityTitle}>Download your letters and statements</h3>
            <p className={s.cardText}>
              Every award letter stays here as a PDF. Once Payroll pays an award, its earning
              statement is added next to it.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

/** FR1 "Check your details": what the directory says, and who to tell if it's wrong. */
function DetailsCard({ me, mine }: { me: Me; mine: MyRecognition }): ReactElement {
  const rows: [string, string | null][] = [
    ['Sector', me.person.sector?.name ?? null],
    ['Manager', mine.details.manager?.name ?? null],
    ['EE#', me.person.eeNumber],
  ];
  return (
    <Card
      title="Check your details"
      titleSize="sm"
      compact
      aside="From the directory"
      help={{
        shows:
          'What the company directory says about you. Your awards are set up from these details.',
        canDo: 'If something is wrong, tell the Stock Admin team so they can fix your record.',
      }}
    >
      <div className={s.detailsHead}>
        <span className={s.detailsAvatar} aria-hidden="true">
          {me.person.initials}
        </span>
        <span className={s.personText}>
          <span className={s.detailsName}>{me.person.displayName}</span>
          {mine.details.joined && (
            <span className={s.meta}>Joined {formatDate(mine.details.joined)}</span>
          )}
        </span>
      </div>
      <dl className={s.details}>
        {rows.map(([term, value]) =>
          value === null ? null : (
            <div key={term} className={s.detailRow}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ),
        )}
      </dl>
      <p className={s.meta}>
        Something wrong?{' '}
        {mine.contact ? `Contact ${mine.contact.person.name}.` : 'Contact the Stock Admin team.'}
      </p>
    </Card>
  );
}
